import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Input,
  Label,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  Form,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";

// Example API call function, implement accordingly
import { fetchItemById, updateItemApi } from "../../services/apiServices";

const EditItem = () => {
  const { id } = useParams(); // assuming route param is id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const getItem = async () => {
      try {
        setLoading(true);
        const res = await fetchItemById(id);
        const item = res?.data?.data || res?.data || res;

        setName(item.name || "");
        setAmount(item.amount || "");
        setExistingImage(item.image || item.wallpaper || ""); // adjust key as per your model
      } catch (err) {
        console.error(err);
        setError("Failed to fetch item details");
      } finally {
        setLoading(false);
      }
    };

    getItem();
  }, [id]);

  const onFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setExistingImage(URL.createObjectURL(selectedFile)); // show preview immediately
    }
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    if (!amount) {
      toast.error("Amount is required");
      return;
    }
    if (!existingImage && !file) {
      toast.error("Please upload an image");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // Prepare form data to match backend expectations
      const formData = new FormData();
      formData.append("id", id);
      formData.append("name", name);
      formData.append("amount", amount);
      if (file) {
        formData.append("file", file);
      } else {
        formData.append("image", existingImage);
      }

      const res = await updateItemApi(formData);
      if (res.status === 200 && res.data?.status) {
        toast.success(res.data.message || "Item updated successfully");
        navigate(-1);
      } else {
        toast.error(res.data?.message || "Failed to update item");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update item");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
      </div>
    );
  }

  if (error) {
    return <Alert color="danger">{error}</Alert>;
  }

  return (
    <div className="page-content d-flex justify-content-center">
      <Card className="shadow-sm w-50">
        <CardHeader className="d-flex justify-content-between align-items-center bg-primary text-white">
          <h5 className="mb-0">Edit Pack Item</h5>
          <Button color="secondary" size="sm" onClick={() => navigate(-1)}>
            Back
          </Button>
        </CardHeader>
        <CardBody>
          <Form>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter item name"
                required
              />
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Amount</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                required
              />
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Image Preview</Label>
              {existingImage ? (
                <div style={{ margin: "8px 0" }}>
                  <img
                    src={existingImage}
                    alt="Item"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              ) : (
                <p>No image available</p>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Upload New Image (optional)</Label>
              <Input type="file" accept="image/*" onChange={onFileChange} />
            </FormGroup>

            <div className="d-flex justify-content-end mt-4">
              <Button
                color="primary"
                onClick={handleUpdate}
                disabled={submitting}>
                {submitting ? "Updating..." : "Update Item"}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default EditItem;
